import { useState, useEffect, useCallback } from "react";
import api from "../services/api";
import { useAuth } from "../contexts/AuthContext";

const ACTION_ICONS = {
  login:           "🔐",
  logout:          "👋",
  invoice_create:  "📄",
  invoice_update:  "✏️",
  invoice_delete:  "🗑️",
  payment_receive: "💰",
  expense_approve: "✅",
  export_fec:      "📊",
  user_create:     "👤",
  access_create:   "🔑",
  access_revoke:   "🚫",
  settings_update: "⚙️",
};

export default function Securite({ currentCompanyId }) {
  const { user } = useAuth();
  const [logs, setLogs]           = useState([]);
  const [stats, setStats]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState("");
  const [qrCode, setQrCode]       = useState(null);
  const [totpToken, setTotpToken] = useState("");
  const [twoFaMsg, setTwoFaMsg]   = useState("");
  const [setupStep, setSetupStep] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.audit.list({ company_id: currentCompanyId }),
      api.audit.stats(),
    ]).then(([res, s]) => {
      setLogs(res.logs || []);
      setStats(s);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [currentCompanyId]);

  useEffect(() => { load(); }, [load]);

  const handleSetup2FA = async () => {
    try {
      const res = await api.auth.setup2FA();
      setQrCode(res.qrCode);
      setSetupStep("scan");
    } catch (err) { console.error(err); }
  };

  const handleVerify2FA = async () => {
    setTwoFaMsg("");
    try {
      await api.auth.verify2FA(totpToken);
      setTwoFaMsg("✅ Authentification à deux facteurs activée !");
      setSetupStep(null);
      setQrCode(null);
      setTotpToken("");
    } catch (err) { setTwoFaMsg(err.message); }
  };

  const handleDisable2FA = async () => {
    if (!window.confirm("Désactiver la 2FA ?")) return;
    try {
      await api.auth.disable2FA(totpToken);
      setTwoFaMsg("2FA désactivée.");
      setTotpToken("");
    } catch (err) { setTwoFaMsg(err.message); }
  };

  const filtered = filter
    ? logs.filter(l => l.action?.includes(filter) || l.user_name?.toLowerCase().includes(filter.toLowerCase()))
    : logs;

  const score = stats?.security_score || 85;

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: "var(--text-muted)" }}>Chargement…</div>;

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Sécurité & Audit trail</h2>
        <button className="btn" onClick={load}>🔄 Actualiser</button>
      </div>

      <div className="kpi-grid k4">
        <div className="kpi">
          <div className="kpi-label">Score de sécurité</div>
          <div className="kpi-value" style={{ color: score >= 80 ? "var(--g)" : score >= 60 ? "var(--amber)" : "var(--red)" }}>{score}/100</div>
        </div>
        <div className="kpi"><div className="kpi-label">Événements 24h</div><div className="kpi-value">{stats?.today_count || logs.length}</div></div>
        <div className="kpi"><div className="kpi-label">Utilisateurs actifs</div><div className="kpi-value">{stats?.active_users || 1}</div></div>
        <div className="kpi"><div className="kpi-label">Alertes</div><div className="kpi-value" style={{ color: stats?.alerts > 0 ? "var(--red)" : "var(--g)" }}>{stats?.alerts || 0}</div></div>
      </div>

      <div className="g2">
        {/* Panel 2FA */}
        <div className="card">
          <div className="card-header"><span className="card-title">🔐 Authentification à deux facteurs</span></div>

          {twoFaMsg && (
            <div className={`alert ${twoFaMsg.startsWith("✅") ? "success" : "danger"}`} style={{ marginBottom: 12 }}>
              {twoFaMsg}
            </div>
          )}

          {!setupStep && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: user?.two_fa_enabled ? "var(--gl)" : "var(--red-light)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
                  {user?.two_fa_enabled ? "🔒" : "🔓"}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{user?.two_fa_enabled ? "2FA activée" : "2FA non activée"}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                    {user?.two_fa_enabled ? "Votre compte est protégé par TOTP (Google Authenticator, Authy…)" : "Activez la 2FA pour renforcer la sécurité de votre compte."}
                  </div>
                </div>
              </div>
              {!user?.two_fa_enabled ? (
                <button className="btn primary" onClick={handleSetup2FA}>🚀 Activer la 2FA</button>
              ) : (
                <div>
                  <div className="form-group" style={{ marginBottom: 8 }}>
                    <label className="form-label">Mot de passe pour désactiver</label>
                    <input className="form-control" type="password" placeholder="••••••••" value={totpToken} onChange={e => setTotpToken(e.target.value)} />
                  </div>
                  <button className="btn" style={{ color: "var(--red)" }} onClick={handleDisable2FA}>Désactiver la 2FA</button>
                </div>
              )}
            </div>
          )}

          {setupStep === "scan" && qrCode && (
            <div>
              <div className="alert info" style={{ marginBottom: 12 }}><span>📱</span> Scannez ce QR code avec Google Authenticator, Authy ou toute app TOTP.</div>
              <div style={{ textAlign: "center", marginBottom: 16 }}>
                <img src={qrCode} alt="QR 2FA" style={{ width: 180, borderRadius: 8 }} />
              </div>
              <div className="form-group">
                <label className="form-label">Code de vérification (6 chiffres)</label>
                <input className="form-control" maxLength={6} placeholder="000000" value={totpToken} onChange={e => setTotpToken(e.target.value)} />
              </div>
              <div className="btn-group" style={{ justifyContent: "flex-end" }}>
                <button className="btn" onClick={() => { setSetupStep(null); setQrCode(null); }}>Annuler</button>
                <button className="btn primary" onClick={handleVerify2FA} disabled={totpToken.length < 6}>✓ Vérifier et activer</button>
              </div>
            </div>
          )}

          <div style={{ marginTop: 16, paddingTop: 12, borderTop: "0.5px solid #f3f4f6" }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>🖥️ Sessions actives</div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span>💻</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>Cet appareil</div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>Maintenant</div>
                </div>
              </div>
              <span className="badge success" style={{ fontSize: 9 }}>Active</span>
            </div>
          </div>
        </div>

        {/* Audit trail */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">📋 Journal d'audit</span>
            <input
              className="form-control"
              style={{ width: 200, fontSize: 12, height: 30, padding: "0 10px" }}
              placeholder="Filtrer…"
              value={filter}
              onChange={e => setFilter(e.target.value)}
            />
          </div>
          <div style={{ maxHeight: 420, overflowY: "auto" }}>
            {filtered.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--text-muted)", padding: 16 }}>Aucun événement</div>
            ) : filtered.slice(0, 50).map((log, i) => (
              <div key={log.id || i} style={{ padding: "9px 0", borderBottom: "0.5px solid #f9fafb", display: "flex", alignItems: "flex-start", gap: 10 }}>
                <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{ACTION_ICONS[log.action] || "📝"}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{log.action?.replace(/_/g, " ")}</div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                    {log.user_name || "Système"}
                    {log.resource && <span style={{ marginLeft: 5, fontFamily: "monospace", fontSize: 10 }}>· {log.resource}</span>}
                  </div>
                </div>
                <div style={{ fontSize: 10, color: "var(--text-muted)", flexShrink: 0 }}>
                  {log.created_at ? new Date(log.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Rôles & Permissions */}
      <div className="card" style={{ marginTop: 20 }}>
        <div className="card-header" style={{ marginBottom: 16 }}>
          <span className="card-title">🎭 Rôles & Permissions</span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
          {/* Admin */}
          <div style={{ border: "1px solid #185fa530", borderRadius: 10, padding: "14px 16px", background: "#185fa508" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 22 }}>👑</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>Administrateur</div>
                <div style={{ fontSize: 11, color: "#185fa5" }}>Accès complet · JWT role = <code style={{ background: "#e5e7eb", padding: "1px 4px", borderRadius: 3 }}>admin</code></div>
              </div>
            </div>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Tous les modules, toutes les actions, gestion des utilisateurs, paramètres entreprise.</div>
          </div>

          {/* Comptable */}
          <div style={{ border: "1px solid #1a9e7230", borderRadius: 10, padding: "14px 16px", background: "#1a9e7208" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 22 }}>📊</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>Comptable</div>
                <div style={{ fontSize: 11, color: "#1a9e72" }}>Finance · JWT role = <code style={{ background: "#e5e7eb", padding: "1px 4px", borderRadius: 3 }}>accountant</code></div>
              </div>
            </div>
            <div style={{ fontSize: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>✅ Autorisé</div>
              <ul style={{ margin: 0, paddingLeft: 16, color: "var(--text-muted)", lineHeight: 1.8 }}>
                <li>Facturation (lecture + écriture)</li>
                <li>Comptabilité complète</li>
                <li>Exports FEC / rapports</li>
                <li>Achats (lecture)</li>
              </ul>
              <div style={{ fontWeight: 600, marginTop: 8, marginBottom: 4, color: "#e24b4a" }}>✕ Interdit</div>
              <ul style={{ margin: 0, paddingLeft: 16, color: "#e24b4a88", lineHeight: 1.8 }}>
                <li>Gestion des utilisateurs</li>
                <li>Paramètres entreprise</li>
              </ul>
            </div>
          </div>

          {/* Manager */}
          <div style={{ border: "1px solid #0ea5e930", borderRadius: 10, padding: "14px 16px", background: "#0ea5e908", position:"relative" }}>
            <div style={{ position:"absolute", top:10, right:12, fontSize:10, background:"#0ea5e9", color:"#fff", padding:"2px 6px", borderRadius:6, fontWeight:700 }}>pwa_role</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 22 }}>🏆</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>Manager Commercial</div>
                <div style={{ fontSize: 11, color: "#0ea5e9" }}>Encadrement équipe · <code style={{ background: "#e5e7eb", padding: "1px 4px", borderRadius: 3 }}>pwa_role = manager</code></div>
              </div>
            </div>
            <div style={{ fontSize: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>✅ Autorisé</div>
              <ul style={{ margin: 0, paddingLeft: 16, color: "var(--text-muted)", lineHeight: 1.8 }}>
                <li>CRM — tous les contacts (sans filtre)</li>
                <li>Onglet Équipe : comparatif + graphiques CA</li>
                <li>Affectation contact → commercial</li>
                <li>Contacts non assignés (alerte + assignation)</li>
                <li>Alertes inactivité commerciaux +30j</li>
                <li>PWA mobile (accès commerciaux)</li>
              </ul>
              <div style={{ marginTop: 10, padding: "8px 10px", background: "#f0f9ff", border: "1px solid #0ea5e930", borderRadius: 6, fontSize: 11 }}>
                <div style={{ fontWeight: 700, marginBottom: 2, color: "#0ea5e9" }}>Config utilisateur</div>
                <code style={{ fontSize: 10 }}>{`role=gestionnaire + pwa_role=manager`}</code>
              </div>
            </div>
          </div>

          {/* Commercial */}
          <div style={{ border: "1px solid #7c3aed30", borderRadius: 10, padding: "14px 16px", background: "#7c3aed08", position:"relative" }}>
            <div style={{ position:"absolute", top:10, right:12, fontSize:10, background:"#7c3aed", color:"#fff", padding:"2px 6px", borderRadius:6, fontWeight:700 }}>pwa_role</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 22 }}>💼</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>Commercial</div>
                <div style={{ fontSize: 11, color: "#7c3aed" }}>Ventes terrain · <code style={{ background: "#e5e7eb", padding: "1px 4px", borderRadius: 3 }}>pwa_role = commercial</code></div>
              </div>
            </div>
            <div style={{ fontSize: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>✅ Autorisé</div>
              <ul style={{ margin: 0, paddingLeft: 16, color: "var(--text-muted)", lineHeight: 1.8 }}>
                <li>CRM — <strong>uniquement ses contacts assignés</strong></li>
                <li>Onglet Portefeuille (ses clients + CA)</li>
                <li>Facturation (lecture)</li>
                <li>Commandes clients (lecture, filtré par commercial_id)</li>
                <li>PWA mobile : clients, commandes, catalogue, picking</li>
              </ul>
              <div style={{ fontWeight: 600, marginTop: 8, marginBottom: 4, color: "#e24b4a" }}>✕ Restreint</div>
              <ul style={{ margin: 0, paddingLeft: 16, color: "#e24b4a88", lineHeight: 1.8 }}>
                <li>Contacts des autres commerciaux (masqués)</li>
                <li>Onglet Équipe (non visible)</li>
                <li>Comptabilité, banque, trésorerie</li>
              </ul>
              <div style={{ marginTop: 10, padding: "8px 10px", background: "#f5f3ff", border: "1px solid #7c3aed30", borderRadius: 6, fontSize: 11 }}>
                <div style={{ fontWeight: 700, marginBottom: 2, color: "#7c3aed" }}>Config utilisateur</div>
                <code style={{ fontSize: 10 }}>{`role=gestionnaire + pwa_role=commercial`}</code>
              </div>
            </div>
          </div>

          {/* Logisticien */}
          <div style={{ border: "1px solid #d9770630", borderRadius: 10, padding: "14px 16px", background: "#d9770608", position: "relative" }}>
            <div style={{ position: "absolute", top: 10, right: 12, fontSize: 10, background: "#d97706", color: "#fff", padding: "2px 6px", borderRadius: 6, fontWeight: 700 }}>NOUVEAU</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 22 }}>🚚</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>Logisticien</div>
                <div style={{ fontSize: 11, color: "#d97706" }}>Stocks & expéditions · JWT role = <code style={{ background: "#e5e7eb", padding: "1px 4px", borderRadius: 3 }}>logistician</code></div>
              </div>
            </div>
            <div style={{ fontSize: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>✅ Autorisé</div>
              <ul style={{ margin: 0, paddingLeft: 16, color: "var(--text-muted)", lineHeight: 1.8 }}>
                <li>Stocks — catalogue (lecture)</li>
                <li>Stocks — mouvements (lecture)</li>
                <li>Préparation de commandes (lecture + écriture)</li>
                <li>Commandes clients (lecture + statut)</li>
                <li>Bons de livraison & expéditions</li>
              </ul>
              <div style={{ fontWeight: 600, marginTop: 8, marginBottom: 4, color: "#e24b4a" }}>✕ Interdit</div>
              <ul style={{ margin: 0, paddingLeft: 16, color: "#e24b4a88", lineHeight: 1.8 }}>
                <li>Comptabilité & facturation</li>
                <li>Données financières (banque, trésorerie)</li>
                <li>Gestion des utilisateurs</li>
                <li>CRM & commissions</li>
              </ul>
            </div>
            <div style={{ marginTop: 10, padding: "8px 10px", background: "#fff7ed", border: "1px solid #d9770630", borderRadius: 6, fontSize: 11 }}>
              <div style={{ fontWeight: 700, marginBottom: 4, color: "#d97706" }}>Claim JWT</div>
              <code style={{ fontSize: 10, color: "#374151" }}>{`{ "role": "logistician", "permissions": ["stocks:read", "preparation:write", "orders:read"] }`}</code>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 16, padding: "10px 14px", background: "#f9fafb", borderRadius: 8, fontSize: 12, color: "var(--text-muted)" }}>
          ℹ️ Deux systèmes de rôles coexistent : <code style={{ background:"#e5e7eb", padding:"1px 4px", borderRadius:3 }}>role</code> (accès backend global) et <code style={{ background:"#e5e7eb", padding:"1px 4px", borderRadius:3 }}>pwa_role</code> (scoping CRM + accès PWA mobile).
          Pour les commerciaux et managers, définissez <code style={{ background:"#e5e7eb", padding:"1px 4px", borderRadius:3 }}>pwa_role = commercial | manager</code> dans la table <code style={{ background:"#e5e7eb", padding:"1px 4px", borderRadius:3 }}>users</code>.
          Le CRM filtre automatiquement les contacts selon <code style={{ background:"#e5e7eb", padding:"1px 4px", borderRadius:3 }}>pwa_role</code> du token JWT.
        </div>
      </div>
    </div>
  );
}
